import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, LocalHospital, CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { isGoogleSignInEnabled } from '../config/amplify';
import { useSnackbar } from 'notistack';
import { portalService } from '../services/portalService';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle, ssoError, completeNewPassword, requiresNewPassword, tempUserEmail } = useAuth();
  // Returning from Google: Amplify is exchanging the ?code for tokens
  const completingSso = !ssoError && new URLSearchParams(window.location.search).has('code');
  const [redirectingToGoogle, setRedirectingToGoogle] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const passwordRequirements = [
    { text: 'At least 8 characters', met: newPassword.length >= 8 },
    { text: 'Contains uppercase letter', met: /[A-Z]/.test(newPassword) },
    { text: 'Contains lowercase letter', met: /[a-z]/.test(newPassword) },
    { text: 'Contains number', met: /\d/.test(newPassword) },
    { text: 'Contains special character', met: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword) },
  ];

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      enqueueSnackbar('Login successful!', { variant: 'success' });
      navigate('/');
    } catch (err: any) {
      console.error('Login error:', err);
      if (err.message === 'NEW_PASSWORD_REQUIRED') {
        // This will be handled by the component re-render showing the new password form
        return;
      }
      setError(err.message || 'Invalid email or password');
      enqueueSnackbar('Login failed. Please check your credentials.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate password requirements
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!passwordRequirements.every(req => req.met)) {
      setError('Password does not meet requirements');
      return;
    }

    setLoading(true);

    try {
      await completeNewPassword(newPassword);
      enqueueSnackbar('Password updated successfully! Welcome to DrEase.', { variant: 'success' });
      navigate('/');
    } catch (err: any) {
      console.error('Password change error:', err);
      setError(err.message || 'Failed to update password');
      enqueueSnackbar('Failed to update password. Please try again.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // A patient who opened their health-records link on this phone
  const portalToken = portalService.savedToken();
  React.useEffect(() => {
    // Installed as an app from that link: go straight to the records
    if (portalToken && window.matchMedia?.('(display-mode: standalone)').matches) navigate(`/my/${portalToken}`, { replace: true });
  }, [portalToken, navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 3,
            background: 'white',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <LocalHospital sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              MedEase
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Healthcare Management Platform for Doctors in India
            </Typography>
          </Box>

          {portalToken && (
            <Alert severity="info" sx={{ mb: 3 }} action={<Button color="inherit" size="small" onClick={() => navigate(`/my/${portalToken}`)}>Open</Button>}>
              Patient? Open your health records.
            </Alert>
          )}

          {(error || ssoError) && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error || ssoError}
            </Alert>
          )}

          {completingSso && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, py: 4 }}>
              <CircularProgress size={24} />
              <Typography color="text.secondary">Signing you in with Google…</Typography>
            </Box>
          )}

          {requiresNewPassword ? (
            <>
              <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
                Set New Password
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
                Welcome! Please set a new password for your account: <strong>{tempUserEmail}</strong>
              </Typography>

              <form onSubmit={handleNewPasswordSubmit}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  margin="normal"
                  required
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin="normal"
                  required
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ mt: 2, mb: 3 }}>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Password Requirements:
                  </Typography>
                  <List dense>
                    {passwordRequirements.map((req, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CheckCircle
                            sx={{
                              color: req.met ? 'success.main' : 'text.disabled',
                              fontSize: 16
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={req.text}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontSize: '0.875rem',
                              color: req.met ? 'success.main' : 'text.secondary'
                            }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || !passwordRequirements.every(req => req.met)}
                  sx={{ mt: 2, mb: 2, py: 1.5 }}
                >
                  {loading ? 'Setting Password...' : 'Set Password & Sign In'}
                </Button>
              </form>
            </>
          ) : (
            <form onSubmit={handleLoginSubmit} hidden={completingSso}>
              {isGoogleSignInEnabled && (
                <>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    disabled={redirectingToGoogle}
                    onClick={async () => {
                      setError('');
                      setRedirectingToGoogle(true);
                      try {
                        await loginWithGoogle();
                      } catch (err: any) {
                        setRedirectingToGoogle(false);
                        setError(err.message || 'Could not start Google sign-in');
                      }
                    }}
                    startIcon={
                      <Box component="svg" viewBox="0 0 48 48" sx={{ width: 20, height: 20 }} aria-hidden>
                        <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.9 6.1C12.5 13.6 17.8 9.5 24 9.5z" />
                        <path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-3.1-.4-4.6H24v9h12.4c-.5 2.9-2.2 5.3-4.6 6.9l7.4 5.7c4.3-4 6.9-9.9 6.9-17z" />
                        <path fill="#FBBC05" d="M10.6 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C1 16.6 0 20.2 0 24s1 7.4 2.7 10.7l7.9-6.1z" />
                        <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.7c-2.1 1.4-4.8 2.3-8.5 2.3-6.2 0-11.5-4.1-13.4-9.9l-7.9 6.1C6.6 42.6 14.6 48 24 48z" />
                      </Box>
                    }
                    sx={{ mt: 1, py: 1.5, textTransform: 'none', fontWeight: 500 }}
                  >
                    {redirectingToGoogle ? 'Redirecting to Google…' : 'Continue with Google'}
                  </Button>
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="caption" color="text.secondary">or use email</Typography>
                  </Divider>
                </>
              )}
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                autoComplete="email"
                autoFocus={!isGoogleSignInEnabled}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mt: 3, mb: 2, py: 1.5 }}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Don't have an account?{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={() => navigate('/register')}
                    sx={{ textDecoration: 'none', fontWeight: 500 }}
                  >
                    Register here
                  </Link>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <Link
                    component="button"
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    sx={{ textDecoration: 'none', fontWeight: 500 }}
                  >
                    Forgot your password?
                  </Link>
                </Typography>
              </Box>
            </form>
          )}

          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              DISHA Compliant • ABHA Integrated • Secure & Private
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;
